import cookies from "js-cookie";
import storageManifest from "../../../../storageManifest.json";
import { storageCanBeSet } from "@JohnLewisPartnership/pecr-util";
import {
  CurtainConfiguration,
  RomanBlindConfiguration,
  LiningWeight,
  HeadingType,
  StackingStyle,
} from "../configurationTypes";
import {
  M2M_CURTAINS_DEFAULT_EYELET_COLOUR,
  M2M_CURTAINS_DEFAULT_HEADING_TYPE,
  M2M_DEFAULT_LINING_WEIGHT,
  M2M_ROMAN_BLINDS_DEFAULT_STACKING_STYLE,
} from "./constants";

const m2mCurtainsCookieName = "m2mCurtainChoices";
const m2mRomanBlindsCookieName = "m2mRomanBlindChoices";

type M2MCookieName =
  | typeof m2mCurtainsCookieName
  | typeof m2mRomanBlindsCookieName;

function isLiningWeight(value = ""): value is LiningWeight {
  return Object.values<string>(LiningWeight).includes(value);
}

function isHeadingType(value = ""): value is HeadingType {
  return Object.values<string>(HeadingType).includes(value);
}

function isStackingStyle(value = ""): value is StackingStyle {
  return Object.values<string>(StackingStyle).includes(value);
}

export function getConfigFromCurtainsCookie(): CurtainConfiguration | null {
  const cookieValue = getCookie(m2mCurtainsCookieName);
  if (!cookieValue || cookieValue.length < 7) {
    return null;
  }

  return {
    type: "Curtain",
    lengthOfTrackOrPole: parseFloat(cookieValue[0] || ""),
    eyeDrop: parseFloat(cookieValue[1] || ""),
    typeOfCurtain: cookieValue[2] || "",
    liningColour: cookieValue[3] || "",
    liningWeight: isLiningWeight(cookieValue[4])
      ? cookieValue[4]
      : M2M_DEFAULT_LINING_WEIGHT,
    headingType: isHeadingType(cookieValue[5])
      ? cookieValue[5]
      : M2M_CURTAINS_DEFAULT_HEADING_TYPE,
    eyeletColour: cookieValue[6] || "",
  };
}

export function getConfigFromRomanBlindsCookie(): RomanBlindConfiguration | null {
  const cookieValue = getCookie(m2mRomanBlindsCookieName);
  if (!cookieValue || cookieValue.length < 7) {
    return null;
  }

  return {
    type: "RomanBlind",
    height: parseFloat(cookieValue[0] || ""),
    width: parseFloat(cookieValue[1] || ""),
    stackingStyle: isStackingStyle(cookieValue[2])
      ? cookieValue[2]
      : M2M_ROMAN_BLINDS_DEFAULT_STACKING_STYLE,
    liningColour: cookieValue[3] || "",
    liningWeight: isLiningWeight(cookieValue[4])
      ? cookieValue[4]
      : M2M_DEFAULT_LINING_WEIGHT,
    controlSide: cookieValue[5] || "",
    installationHeight: parseFloat(cookieValue[6] || ""),
  };
}

export function setCurtainsCookieFromConfig(config: CurtainConfiguration) {
  if (!config) {
    return;
  }
  const cookieValue = `${config?.lengthOfTrackOrPole};${config?.eyeDrop};${config?.typeOfCurtain};${config?.liningColour};${config?.liningWeight};${config?.headingType};${config?.eyeletColour ? config?.eyeletColour : M2M_CURTAINS_DEFAULT_EYELET_COLOUR}`;
  setCookie(m2mCurtainsCookieName, cookieValue);
}

export function setRomanBlindsCookieFromConfig(
  config: RomanBlindConfiguration,
) {
  if (!config) {
    return;
  }
  const cookieValue = `${config?.height};${config?.width};${config?.stackingStyle};${config?.liningColour};${config?.liningWeight};${config?.controlSide};${config?.installationHeight}`;
  setCookie(m2mRomanBlindsCookieName, cookieValue);
}

function getCookie(cookieName: M2MCookieName) {
  if (typeof window !== "undefined") {
    const encodedCookie = cookies.get(cookieName);
    try {
      if (encodedCookie) {
        return window.atob(encodedCookie).split(";");
      }
    } catch (err) {
      return null;
    }
  }
  return null;
}

function setCookie(cookieName: M2MCookieName, cookieValue: string) {
  if (
    cookieName &&
    cookieValue &&
    storageCanBeSet(storageManifest.cookie[cookieName])
  ) {
    cookies.set(cookieName, window.btoa(cookieValue), {
      path: "/",
      expires: 365,
      secure: true,
    });
  }
}
